// src/components/accounts/CreateAccountPanel.js
// DROP-IN REPLACEMENT - FIXED FORM SUBMISSION
//
// ✅ Fixed form submission - removed onClick from footer button
// ✅ Fixed input focus issue (form was re-submitting on every keystroke)
// ✅ Territory Auto = based on address (zip → city+state)
// ✅ If no match: territoryId/territoryName are null
// ✅ Admin/Owner can manually override territory anytime
// ✅ EndUser: assignedTo locked to self; groups hidden; territory shown as autodetect preview
// ✅ EndUser: account can save even if no groups exist / cannot read groups
// ✅ Groups saved as: (endUser) my user.groupIds if present, otherwise [] (or staffGroupId if available)

import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  serverTimestamp,
  getDocs,
  getDoc,
  query,
  where,
  addDoc,
} from "firebase/firestore";
import { X, Users } from "lucide-react";
import { db } from "../../firebase";
import { getTerritoryLabel, resolveAccountTerritory } from "../../utils/accountTerritory";

const STAGES = ["Prospect", "Sampling", "Customer", "Churn Risk", "Inactive"];
const FLAGS = ["None", "Sample Abuse", "Repeat Samples", "Converted Orders", "Do Not Serve"];
const AUTO_TERRITORY = "__AUTO__";

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// --- Helpers ---
const uniqStrings = (arr) =>
  Array.from(new Set((Array.isArray(arr) ? arr : []).filter(Boolean).map(String)));

const roleNorm = (v) => String(v || "").trim().toLowerCase();
const isEndUserRole = (r) => ["enduser", "end_user"].includes(roleNorm(r));
const isAdminRole = (r) => ["admin", "owner"].includes(roleNorm(r));

// Compact row like InventoryCreateModal - MOVED OUTSIDE COMPONENT
const Row = ({ label, required, children }) => (
  <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2">
    <label className="text-[11px] font-medium text-slate-700">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    <div className="min-w-0">{children}</div>
  </div>
);

const CreateAccountPanel = ({ isOpen, onClose, companyId, onCreated }) => {
  const auth = getAuth();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [companyData, setCompanyData] = useState(null);
  const [me, setMe] = useState(null);

  const [teamUsers, setTeamUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);

  const [customValues, setCustomValues] = useState({});
  const [groupSearch, setGroupSearch] = useState("");

  const [form, setForm] = useState({
    name: "",
    locationPhone: "",

    assignedToId: "",
    territoryOverride: false,
    territoryId: "",
    groupIds: [],

    primaryContactName: "",
    primaryContactEmail: "",

    stage: "Prospect",
    flaggedAs: "None",
    visited: false,
    lastVisitedDate: "",
    hasSampleOrder: false,
    active: true,
    notes: "",

    addressStreet: "",
    addressStreet2: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
  });

  const myRole = useMemo(() => roleNorm(me?.role), [me]);
  const isEndUser = useMemo(() => isEndUserRole(myRole), [myRole]);
  const isAdminOwner = useMemo(() => isAdminRole(myRole), [myRole]);

  const myDisplayName = useMemo(() => {
    return (
      me?.fullName ||
      me?.displayName ||
      me?.name ||
      me?.email ||
      auth.currentUser?.email ||
      auth.currentUser?.uid ||
      "You"
    );
  }, [me, auth]);

  const teamUsersById = useMemo(() => {
    const m = new Map();
    teamUsers.forEach((u) => m.set(u.id, u));
    return m;
  }, [teamUsers]);

  const territoriesById = useMemo(() => {
    const m = new Map();
    territories.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [territories]);

  const filteredGroups = useMemo(() => {
    const q = (groupSearch || "").toLowerCase().trim();
    if (!q) return groups;
    return groups.filter((g) => (g?.name || "").toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const allStaffGroupId = useMemo(() => {
    const id = companyData?.staffGroupId ? String(companyData.staffGroupId) : "";
    return id;
  }, [companyData]);

  // Auto territory preview (from address fields)
  const autoTerritoryPreview = useMemo(() => {
    const assignedUser =
      !isEndUser && form.assignedToId ? teamUsersById.get(form.assignedToId) : me;
    return resolveAccountTerritory({
      territories,
      address: {
        addressZip: form.addressZip,
        addressCity: form.addressCity,
        addressState: form.addressState,
      },
      assignedUser,
    });
  }, [
    form.addressZip,
    form.addressCity,
    form.addressState,
    form.assignedToId,
    isEndUser,
    me,
    teamUsersById,
    territories,
  ]);

  const autoTerritoryDetail = useMemo(() => {
    if (!autoTerritoryPreview?.matchedBy) return "No territory match yet";
    if (autoTerritoryPreview.matchedBy === "assigned_default") {
      return "Using the selected rep's assigned territory";
    }
    if (autoTerritoryPreview.matchedBy.startsWith("assigned_")) {
      return "Matched within the selected rep's assigned territories";
    }
    if (autoTerritoryPreview.matchedBy === "zip") {
      return "Matched by ZIP";
    }
    if (autoTerritoryPreview.matchedBy === "city") {
      return "Matched by city";
    }
    if (autoTerritoryPreview.matchedBy === "state") {
      return "Matched by state";
    }
    return "Auto-detected territory";
  }, [autoTerritoryPreview]);

  // --- Helpers ---
  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const handleCustomChange = (fieldKey, value) =>
    setCustomValues((p) => ({ ...p, [fieldKey]: value }));

  const toggleGroup = (groupId) => {
    const gid = String(groupId);
    setForm((prev) => {
      const set = new Set(uniqStrings(prev.groupIds));
      if (set.has(gid)) set.delete(gid);
      else set.add(gid);
      return { ...prev, groupIds: Array.from(set) };
    });
  };

  // Load data when modal opens
  useEffect(() => {
    if (!companyId || !isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const uid = auth.currentUser?.uid || null;

        // Load me (/users/{uid})
        let meDoc = null;
        if (uid) {
          try {
            const meSnap = await getDoc(doc(db, "users", uid));
            meDoc = meSnap.exists() ? { id: meSnap.id, ...meSnap.data() } : { id: uid };
          } catch (e) {
            console.warn("[CreateAccountPanel] Could not read user doc:", e);
            meDoc = { id: uid };
          }
        }
        setMe(meDoc);

        // Load company
        let company = null;
        try {
          const companySnap = await getDoc(doc(db, "companies", companyId));
          company = companySnap.exists() ? { id: companySnap.id, ...companySnap.data() } : null;
        } catch (e) {
          console.warn("[CreateAccountPanel] Could not read company doc:", e);
        }
        setCompanyData(company);

        const r = roleNorm(meDoc?.role);
        const endUser = isEndUserRole(r);
        const adminOwner = isAdminRole(r);

        // Territories (best-effort)
        try {
          const terrSnap = await getDocs(collection(db, "companies", companyId, "territories"));
          const terrList = terrSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setTerritories(terrList);
        } catch (e) {
          setTerritories([]);
        }

        // Field definitions (best-effort)
        try {
          const defsSnap = await getDocs(collection(db, "companies", companyId, "accountFieldDefinitions"));
          const defs = defsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          defs.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
          setFieldDefs(defs);
        } catch (e) {
          setFieldDefs([]);
        }

        // Admin/Owner only: load team users + groups
        if (adminOwner) {
          // Team users (non-customers)
          try {
            const usersSnap = await getDocs(
              query(collection(db, "users"), where("companyId", "==", companyId))
            );
            const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const staff = allUsers
              .filter((u) => roleNorm(u.role) !== "customer")
              .sort((a, b) => {
                const an = (a.fullName || a.displayName || a.email || "").toLowerCase();
                const bn = (b.fullName || b.displayName || b.email || "").toLowerCase();
                return an.localeCompare(bn);
              });
            setTeamUsers(staff);
          } catch (e) {
            setTeamUsers([]);
          }

          // Groups
          try {
            const groupsSnap = await getDocs(collection(db, "companies", companyId, "groups"));
            const groupsList = groupsSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setGroups(groupsList);
          } catch (e) {
            setGroups([]);
          }
        } else {
          // EndUser: do not load groups/users (often denied by rules + not needed)
          setTeamUsers([]);
          setGroups([]);
        }

        // Set defaults (tight + role-aware)
        const defaultAssignedToId = endUser ? (uid || "") : "";
        const defaultGroupIds = (() => {
          if (endUser) {
            const gids = uniqStrings(meDoc?.groupIds);
            if (gids.length) return gids;
            if (company?.staffGroupId) return [String(company.staffGroupId)];
            return [];
          }
          return company?.staffGroupId ? [String(company.staffGroupId)] : [];
        })();

        setForm((prev) => ({
          ...prev,
          assignedToId: defaultAssignedToId,
          groupIds: defaultGroupIds,
          territoryOverride: false, // default to Auto
          territoryId: "",
        }));

        setCustomValues({});
        setGroupSearch("");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [auth, companyId, isOpen]);

  // Reset when closing
  useEffect(() => {
    if (isOpen) return;

    setSaving(false);
    setLoading(true);
    setCustomValues({});
    setGroupSearch("");
    setTeamUsers([]);
    setGroups([]);
    setTerritories([]);
    setFieldDefs([]);
    setCompanyData(null);
    setMe(null);

    setForm({
      name: "",
      locationPhone: "",
      assignedToId: "",
      territoryOverride: false,
      territoryId: "",
      groupIds: [],
      primaryContactName: "",
      primaryContactEmail: "",
      stage: "Prospect",
      flaggedAs: "None",
      visited: false,
      lastVisitedDate: "",
      hasSampleOrder: false,
      active: true,
      notes: "",
      addressStreet: "",
      addressStreet2: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
    });
  }, [isOpen]);

  if (!isOpen) return null;

  const inputBase =
    "w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";
  const inputDisabled = "bg-slate-100 text-slate-500 cursor-not-allowed";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      alert("Location name is required.");
      return;
    }

    setSaving(true);
    try {
      const uid = auth.currentUser?.uid || null;

      // --- Role-locked behavior ---
      const finalAssignedToId = isEndUser ? uid : (form.assignedToId || null);

      // Groups:
      const finalGroupIds = (() => {
        if (isEndUser) {
          const gids = uniqStrings(me?.groupIds);
          if (gids.length) return gids;
          if (allStaffGroupId) return [allStaffGroupId];
          return [];
        }
        return uniqStrings(form.groupIds);
      })();

      // ✅ Territory logic
      // - Manual override (admin/owner): store selected
      // - Auto (default): detect from address; if no match => null
      const assignedUser =
        !isEndUser && finalAssignedToId ? teamUsersById.get(finalAssignedToId) : me;
      const auto = resolveAccountTerritory({
        territories,
        address: {
          addressZip: form.addressZip,
          addressCity: form.addressCity,
          addressState: form.addressState,
        },
        assignedUser,
      });

      let territoryId = null;
      let territoryName = null;
      let territoryOverride = false;

      if (!isEndUser && form.territoryOverride && form.territoryId) {
        const tid = String(form.territoryId);
        const t = territoriesById.get(tid);
        territoryId = tid;
        territoryName = getTerritoryLabel(t) || tid;
        territoryOverride = true;
      } else {
        territoryId = auto?.id ? String(auto.id) : null;
        territoryName = auto?.name || null;
        territoryOverride = false;
      }

      // Custom fields with correct types
      const cleanedCustom = {};
      fieldDefs.forEach((def) => {
        const key = def.fieldKey || def.id;
        const raw = customValues[key];
        if (raw === undefined || raw === null || raw === "") return;

        const type = (def.type || "text").toLowerCase();
        if (type === "number") {
          const n = Number(raw);
          if (!Number.isNaN(n)) cleanedCustom[key] = n;
        } else if (type === "boolean") {
          cleanedCustom[key] = !!raw;
        } else {
          cleanedCustom[key] = raw;
        }
      });

      const contactName = form.primaryContactName.trim() || null;
      const contactEmail = form.primaryContactEmail.trim().toLowerCase() || null;

      let lastVisitedAt = null;
      if (form.visited && form.lastVisitedDate) {
        const d = new Date(form.lastVisitedDate + "T00:00:00");
        lastVisitedAt = Number.isNaN(d.getTime()) ? null : d;
      }

      const assignedUserForLabel =
        !isEndUser && finalAssignedToId ? teamUsersById.get(finalAssignedToId) : null;
      const assignedToName = isEndUser
        ? (me?.fullName || me?.displayName || me?.email || auth.currentUser?.email || null)
        : assignedUserForLabel
          ? assignedUserForLabel.fullName || assignedUserForLabel.displayName || assignedUserForLabel.email || null
          : null;

      const payload = {
        companyId,
        name: trimmedName,
        phone: form.locationPhone.trim() || null,

        assignedToId: finalAssignedToId,
        assignedUserId: finalAssignedToId, // legacy compat
        assignedToName,

        territoryId,
        territoryName,
        territoryOverride,

        groupIds: finalGroupIds,

        primaryContact: {
          name: contactName,
          email: contactEmail,
          phone: null,
          title: null,
        },
        contact: contactName,
        contactName: contactName,
        email: contactEmail,
        contactEmail: contactEmail,

        stage: form.stage || "Prospect",
        flaggedAs: form.flaggedAs || "None",
        visited: !!form.visited,
        lastVisitedAt: form.visited ? lastVisitedAt : null,
        hasSampleOrder: !!form.hasSampleOrder,
        active: form.active !== false,
        notes: form.notes.trim() || null,

        addressStreet: form.addressStreet.trim() || null,
        addressStreet2: form.addressStreet2.trim() || null,
        addressCity: form.addressCity.trim() || null,
        addressState: form.addressState.trim() || null,
        addressZip: form.addressZip.trim() || null,

        city: form.addressCity.trim() || null,
        state: form.addressState.trim() || null,

        customFields: cleanedCustom,

        createdAt: serverTimestamp(),
        createdBy: uid,
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "companies", companyId, "accounts"), payload);

      await onCreated?.();
      onClose?.();
    } catch (err) {
      console.error("[CreateAccountPanel] Error creating account:", err);
      alert(`Could not create location: ${err?.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  const selectedGroupCount = uniqStrings(form.groupIds).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/60" onClick={() => !saving && onClose?.()} />

      <div
        className="relative flex w-full max-w-6xl max-h-[94vh] flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-900">Add Location</h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {isEndUser ? "Assigned to you automatically. Admin can adjust later." : "Create a new account/location."}
            </p>
            {loading ? (
              <div className="mt-1 inline-flex items-center gap-2 text-[11px] text-slate-600">
                Loading company settings…
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"
            disabled={saving}
            title="Close"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <form id="create-account-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 sm:p-6">
          {loading ? (
            <div className="flex min-h-[16rem] items-center justify-center">
              <p className="text-sm text-slate-500">Loading…</p>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3 lg:gap-6">
                {/* LEFT (main) */}
                <div className="min-w-0 space-y-3 sm:space-y-4 lg:col-span-2 lg:space-y-5">
                  {/* Location */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                    <div className="text-sm font-semibold text-slate-900 mb-2">Location</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Row label="Name" required>
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.name}
                          onChange={(e) => setField("name", e.target.value)}
                          placeholder="Restaurant name or location"
                          required
                        />
                      </Row>

                      <Row label="Phone">
                        <input
                          type="tel"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.locationPhone}
                          onChange={(e) => setField("locationPhone", e.target.value)}
                          placeholder="(###) ###-####"
                        />
                      </Row>
                    </div>
                  </div>

                  {/* Primary Contact */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                      <div className="text-sm font-semibold text-slate-900 mb-2">Primary Contact</div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Row label="Contact">
                          <input
                            type="text"
                            className={inputBase}
                            value={form.primaryContactName}
                            onChange={(e) => setField("primaryContactName", e.target.value)}
                            placeholder="John Smith"
                          />
                        </Row>

                        <Row label="Email">
                          <input
                            type="email"
                            className={inputBase}
                            value={form.primaryContactEmail}
                            onChange={(e) => setField("primaryContactEmail", e.target.value)}
                            placeholder="john@restaurant.com"
                          />
                        </Row>
                      </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                    <div className="text-sm font-semibold text-slate-900 mb-2">Relationship &amp; Behavior</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Row label="Stage">
                        <select
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.stage}
                          onChange={(e) => setField("stage", e.target.value)}
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </Row>

                      <Row label="Flag">
                        <select
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.flaggedAs}
                          onChange={(e) => setField("flaggedAs", e.target.value)}
                        >
                          {FLAGS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </Row>

                      <Row
                        label="Visited"
                        right={
                          <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={!!form.visited}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setForm((prev) => ({
                                  ...prev,
                                  visited: checked,
                                  lastVisitedDate:
                                    checked && !prev.lastVisitedDate
                                      ? new Date().toISOString().split("T")[0]
                                      : prev.lastVisitedDate,
                                }));
                              }}
                            />
                          </label>
                        }
                      >
                        {form.visited ? (
                          <input
                            type="date"
                            className={inputBase}
                            value={form.lastVisitedDate}
                            onChange={(e) => setField("lastVisitedDate", e.target.value)}
                          />
                        ) : (
                          <input
                            className={`${inputBase} ${inputDisabled}`}
                            value="Not marked visited"
                            readOnly
                          />
                        )}
                      </Row>

                      <Row label="Sample / Active">
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-700">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={!!form.hasSampleOrder}
                              onChange={(e) => setField("hasSampleOrder", e.target.checked)}
                            />
                            Sample
                          </label>

                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={!!form.active}
                              onChange={(e) => setField("active", e.target.checked)}
                            />
                            Active
                          </label>
                        </div>
                      </Row>

                      <div className="md:col-span-2">
                        <Row label="Notes">
                          <textarea
                            rows={2}
                            className="w-full resize-none rounded-lg border border-slate-300 px-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={form.notes}
                            onChange={(e) => setField("notes", e.target.value)}
                            placeholder="Account history, context..."
                          />
                        </Row>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                    <div className="text-sm font-semibold text-slate-900 mb-2">Address</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Row label="Street">
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.addressStreet}
                          onChange={(e) => setField("addressStreet", e.target.value)}
                          placeholder="123 Main St"
                        />
                      </Row>

                      <Row label="Street 2">
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={form.addressStreet2}
                          onChange={(e) => setField("addressStreet2", e.target.value)}
                          placeholder="Suite 100"
                        />
                      </Row>

                      <div className="md:col-span-2">
                        <Row label="City / State / Zip">
                          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
                            <input
                              type="text"
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={form.addressCity}
                              onChange={(e) => setField("addressCity", e.target.value)}
                              placeholder="City"
                            />
                            <select
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={form.addressState}
                              onChange={(e) => setField("addressState", e.target.value)}
                            >
                              <option value="">State</option>
                              {US_STATES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.value}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={form.addressZip}
                              onChange={(e) => setField("addressZip", e.target.value)}
                              placeholder="Zip"
                            />
                          </div>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT (side) */}
                <div className="min-w-0 space-y-3 sm:space-y-4">
                  {/* Coverage / Territory */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="text-sm font-semibold text-slate-900 mb-2">Coverage</div>

                    {isEndUser ? (
                      <div className="text-sm text-slate-700">
                        <div className="text-[11px] text-slate-500 mb-1">Assigned to</div>
                        <div className="font-medium">{myDisplayName}</div>

                        <div className="mt-3 text-[11px] text-slate-500 mb-1">Territory (Auto)</div>
                        <div className="text-[12px] text-slate-700">
                          {autoTerritoryPreview?.name || "Not detected (will stay blank)"}
                        </div>

                        <div className="mt-3 text-[11px] text-slate-500 mb-1">Groups</div>
                        <div className="text-[12px] text-slate-700">
                          Hidden for end users (admin can adjust later).
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-slate-500 mb-2">
                          Territory is autodetected from address unless manually overridden.
                        </div>

                        <Row label="Territory">
                          <select
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={form.territoryOverride ? form.territoryId : AUTO_TERRITORY}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === AUTO_TERRITORY) {
                                setForm((prev) => ({
                                  ...prev,
                                  territoryOverride: false,
                                  territoryId: "",
                                }));
                                return;
                              }
                              setForm((prev) => ({
                                ...prev,
                                territoryOverride: !!val,
                                territoryId: val || "",
                              }));
                            }}
                          >
                            <option value={AUTO_TERRITORY}>
                              {autoTerritoryPreview?.name
                                ? `Auto-detect: ${autoTerritoryPreview.name}`
                                : "Auto-detect (no match yet)"}
                            </option>
                            {territories.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name || t.label || t.id}
                              </option>
                            ))}
                          </select>
                        </Row>

                        <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2">
                          <span className="truncate">
                            {autoTerritoryPreview?.name || "No territory selected"}
                            {!form.territoryOverride ? ` · ${autoTerritoryDetail}` : ""}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded-full border text-[9px] ${
                              form.territoryOverride
                                ? "bg-amber-50 text-amber-800 border-amber-200"
                                : "bg-blue-50 text-blue-800 border-blue-200"
                            }`}
                          >
                            {form.territoryOverride ? "Manual" : "Auto"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Groups (Admin/Owner only) */}
                  {isAdminOwner && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-700" />
                          <div className="text-sm font-semibold text-slate-900">Groups</div>
                        </div>
                        <div className="text-xs text-slate-600">
                          Selected: <span className="font-semibold text-blue-700">{selectedGroupCount}</span>
                        </div>
                      </div>

                      {groups.length > 0 ? (
                        <>
                          <input
                            value={groupSearch}
                            onChange={(e) => setGroupSearch(e.target.value)}
                            placeholder="Search groups…"
                            className={`${inputBase} mb-2`}
                          />

                          <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl bg-white">
                            <ul className="divide-y divide-slate-100">
                              {filteredGroups.map((g) => {
                                const checked = uniqStrings(form.groupIds).includes(String(g.id));
                                const isAllStaff = allStaffGroupId && String(g.id) === String(allStaffGroupId);

                                return (
                                  <li key={g.id} className="px-3 py-2 hover:bg-slate-50">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={checked}
                                        onChange={() => toggleGroup(g.id)}
                                      />
                                      <span className="text-sm text-slate-800 flex-1 truncate">
                                        {g.name || "Untitled group"}
                                        {isAllStaff ? (
                                          <span className="ml-2 text-xs text-blue-700">(All Staff)</span>
                                        ) : null}
                                      </span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>

                          <div className="mt-2 text-[11px] text-slate-600">
                            Tip: default is All Staff when available (company.staffGroupId).
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-slate-600">
                          No groups found (or not permitted to read groups).
                          <div className="text-xs text-slate-500 mt-1">
                            You can still create the location — groups can be assigned later.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {fieldDefs.length > 0 && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 sm:mt-4 sm:p-4 lg:mt-5">
                  <div className="text-sm font-semibold text-slate-900 mb-2">Custom Fields</div>

                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {fieldDefs
                      .filter((def) => def.showInForm !== false)
                      .map((def) => {
                        const key = def.fieldKey || def.id;
                        const type = (def.type || "text").toLowerCase();
                        const value = customValues[key];

                        if (type === "boolean") {
                          return (
                            <Row key={key} label={def.label || key}>
                              <div className="flex justify-end">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4"
                                  checked={!!value}
                                  onChange={(e) => handleCustomChange(key, e.target.checked)}
                                />
                              </div>
                            </Row>
                          );
                        }

                        if (type === "select") {
                          const options = Array.isArray(def.options)
                            ? def.options
                            : String(def.options || "")
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean);

                          return (
                            <Row key={key} label={def.label || key}>
                              <select
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={value ?? ""}
                                onChange={(e) => handleCustomChange(key, e.target.value)}
                              >
                                <option value="">Select…</option>
                                {options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </Row>
                          );
                        }

                        if (type === "number") {
                          return (
                            <Row key={key} label={def.label || key}>
                              <input
                                type="number"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={value ?? ""}
                                onChange={(e) => handleCustomChange(key, e.target.value)}
                              />
                            </Row>
                          );
                        }

                        return (
                          <Row key={key} label={def.label || key}>
                            <input
                              type="text"
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={value ?? ""}
                              onChange={(e) => handleCustomChange(key, e.target.value)}
                            />
                          </Row>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

        </form>

        <div className="sticky bottom-0 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-account-form"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            disabled={saving || loading}
          >
            {saving ? "Creating..." : "Create Location"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPanel;
